/**
 * Comparison — unit economics, side by side.
 *
 * The Overview answers "what did we spend". This answers "what is expensive",
 * which is a different question and needs a different denominator: total cost
 * ranks by volume, cost-per-session and cost-per-minute rank by efficiency,
 * and they routinely disagree. The agent with the largest bill is often just
 * the busiest one; the agent worth looking at is the one whose sessions cost
 * three times what everyone else's do.
 *
 * Every table here is sortable by unit, and every row carries the denominator
 * it was divided by — a $/min figure over eleven seconds of measured time is
 * arithmetic, not a rate, and the count is what says so.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { ArrowUpDown } from 'lucide-react';
import {
  AdminAPI, UsageBreakdownRow, UsageChannelRow, UsageDimension,
  UsageFilterState, UsageReportSummary, UsageSessionRow,
} from '../../lib/api';
import { CHANNEL_COLOR, CHANNEL_LABEL, Card, Chip, DIMENSION_LABEL, DurationBadge, Empty, MODALITY_COLOR, MODALITY_LABEL, Notice, ShareBar, Table, Td, Th, duration, int, minutes, pct, Money, ExportCsv, downloadCsv, useCurrency } from './shared';

type UnitMetric = 'cost_usd' | 'cost_per_session';

export const ComparisonTab: React.FC<{
  api: AdminAPI;
  filters: UsageFilterState;
  summary: UsageReportSummary | null;
  nonce: number;
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ api, filters, summary, nonce, onDrill }) => {
  const [dimension, setDimension] = useState<UsageDimension>('agent');
  const [metric, setMetric] = useState<UnitMetric>('cost_per_session');
  const [rows, setRows] = useState<UsageBreakdownRow[] | null>(null);
  const [rowsError, setRowsError] = useState<string | null>(null);
  const [expensive, setExpensive] = useState<UsageSessionRow[] | null>(null);
  const [expensiveError, setExpensiveError] = useState<string | null>(null);

  useEffect(() => {
    api.getUsageBreakdown(filters, dimension, 100)
      .then((b) => { setRows(b.rows); setRowsError(null); })
      .catch((e) => { setRows([]); setRowsError(e instanceof Error ? e.message : String(e)); });
  }, [api, filters, dimension, nonce]);

  useEffect(() => {
    api.getUsageSessions(filters, { sort: 'cost_per_minute', limit: 8 })
      .then((r) => { setExpensive(r.rows); setExpensiveError(null); })
      .catch((e) => { setExpensive([]); setExpensiveError(e instanceof Error ? e.message : String(e)); });
  }, [api, filters, nonce]);

  const sorted = useMemo(() => {
    const list = [...(rows ?? [])];
    list.sort((a, b) => {
      const av = metric === 'cost_usd' ? a.cost_usd : (a.cost_per_session ?? -1);
      const bv = metric === 'cost_usd' ? b.cost_usd : (b.cost_per_session ?? -1);
      return bv - av;
    });
    return list.slice(0, 20);
  }, [rows, metric]);

  const peak = sorted.length
    ? Math.max(...sorted.map((r) => (metric === 'cost_usd' ? r.cost_usd : (r.cost_per_session ?? 0))))
    : 0;

  return (
    <div className="space-y-4">
      <ChannelComparison rows={summary?.by_channel ?? []} onDrill={onDrill} />

      <Card
        title="Ranked by unit cost"
        subtitle="Total cost ranks by volume; cost per session ranks by efficiency. They usually disagree, and the second one is the interesting list."
        right={
          <div className="flex items-center gap-2">
            <select
              value={dimension}
              onChange={(e) => setDimension(e.target.value as UsageDimension)}
              className="rounded border-gray-300 py-1 text-xs"
            >
              {(['agent', 'org', 'model', 'feature', 'provider', 'channel'] as UsageDimension[]).map((d) => (
                <option key={d} value={d}>By {DIMENSION_LABEL[d].toLowerCase()}</option>
              ))}
            </select>
            <button
              onClick={() => setMetric(metric === 'cost_usd' ? 'cost_per_session' : 'cost_usd')}
              className="inline-flex items-center gap-1 rounded border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50"
              title="Switch the ranking metric"
            >
              <ArrowUpDown className="h-3 w-3" />
              {metric === 'cost_usd' ? 'Total cost' : 'Cost / session'}
            </button>
            <ExportCsv onClick={() => downloadCsv(`ranked_by_${dimension}_${filters.from}_${filters.to}`, [
              { key: 'label', label: dimension }, { key: 'key', label: 'key' }, { key: 'sessions', label: 'sessions' },
              { key: 'calls', label: 'calls' }, { key: 'cost_usd', label: 'cost_usd' }, { key: 'cost_inr', label: 'cost_inr' },
              { key: 'cost_per_session', label: 'cost_per_session_usd' }, { key: 'share_pct', label: 'share_pct' },
            ], sorted as unknown as Record<string, unknown>[])} />
          </div>
        }
        bodyClassName=""
      >
        <Table
          head={
            <tr>
              <Th>{DIMENSION_LABEL[dimension]}</Th>
              <Th align="right">Sessions</Th>
              <Th align="right">Calls</Th>
              <Th align="right">Cost / session</Th>
              <Th align="right">Total cost</Th>
              <Th className="w-40">{metric === 'cost_usd' ? 'Share of spend' : 'Relative unit cost'}</Th>
            </tr>
          }
        >
          {sorted.map((r) => {
            const value = metric === 'cost_usd' ? r.cost_usd : (r.cost_per_session ?? 0);
            return (
              <tr
                key={r.key ?? 'null'}
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => onDrill(drillFor(dimension, r.key))}
              >
                <Td className="text-gray-900">{r.label}</Td>
                <Td align="right" className="tabular-nums">{int(r.sessions)}</Td>
                <Td align="right" className="tabular-nums text-gray-500">{int(r.calls)}</Td>
                <Td align="right" className="tabular-nums font-medium text-gray-900">
                  <Money value={r.cost_per_session} bare />
                  {/* The denominator, right next to the ratio: a per-session
                      figure over two sessions is a coincidence, not a rate. */}
                  {r.sessions > 0 && r.sessions < 5 && (
                    <span className="ml-1 text-[10px] font-normal text-amber-600" title="Few sessions — this average is not yet stable">
                      n={r.sessions}
                    </span>
                  )}
                </Td>
                <Td align="right" className="tabular-nums text-gray-700"><Money value={r.cost_usd} inr={r.cost_inr} /></Td>
                <Td>
                  <ShareBar pct={peak > 0 ? (value / peak) * 100 : 0} />
                </Td>
              </tr>
            );
          })}
          {rowsError && (
            <Empty colSpan={6}><span className="text-rose-700">Couldn't load the ranking: {rowsError}. Refresh to try again.</span></Empty>
          )}
          {!rowsError && rows === null && <Empty colSpan={6}>Loading…</Empty>}
          {!rowsError && rows !== null && !sorted.length && (
            <Empty colSpan={6}>Nothing in this window. Widen the range or clear a filter.</Empty>
          )}
        </Table>
      </Card>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <Card
          title="Most expensive minutes"
          subtitle="All-in cost per minute (LLM + speech + carrier) over at least 30 measured seconds — a two-second call has no rate"
          right={<ExportCsv onClick={() => downloadCsv(`expensive_minutes_${filters.from}_${filters.to}`, [
            { key: 'session_key', label: 'session' }, { key: 'agent_name', label: 'agent' }, { key: 'channel', label: 'channel' },
            { key: 'started_at', label: 'started_at' }, { key: 'duration_seconds', label: 'duration_seconds' },
            { key: 'cost_usd', label: 'cost_usd' }, { key: 'cost_inr', label: 'cost_inr' },
            { key: 'cost_per_minute', label: 'cost_per_minute_usd' }, { key: 'cost_per_minute_inr', label: 'cost_per_minute_inr' },
            { key: 'llm_per_minute', label: 'llm_per_minute_usd' }, { key: 'tts_per_minute', label: 'tts_per_minute_usd' },
            { key: 'stt_per_minute', label: 'stt_per_minute_usd' }, { key: 'telephony_per_minute', label: 'carrier_per_minute_usd' },
          ], (expensive ?? []) as unknown as Record<string, unknown>[])} />}
          bodyClassName=""
        >
          <Table
            head={
              <tr>
                <Th>Session</Th>
                <Th align="right">Duration</Th>
                <Th align="right">Cost</Th>
                <Th align="right">All-in / min</Th>
              </tr>
            }
          >
            {(expensive ?? []).map((s) => (
              <tr key={s.session_key} className="hover:bg-gray-50">
                <Td>
                  <div className="flex items-center gap-1.5">
                    <Chip kind="channel" value={s.channel} />
                    <span className="truncate text-gray-900">{s.agent_name}</span>
                  </div>
                  <div className="text-[11px] text-gray-500">{s.organization_name || '—'}</div>
                </Td>
                <Td align="right" className="whitespace-nowrap">
                  <div className="tabular-nums">{duration(s.duration_seconds)}</div>
                  <div className="mt-0.5"><DurationBadge source={s.duration_source} /></div>
                </Td>
                <Td align="right" className="tabular-nums"><Money value={s.cost_usd} inr={s.cost_inr} /></Td>
                <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={s.cost_per_minute} inr={s.cost_per_minute_inr} /></Td>
              </tr>
            ))}
            {expensiveError && (
              <Empty colSpan={4}><span className="text-rose-700">Couldn't load these sessions: {expensiveError}. Refresh to try again.</span></Empty>
            )}
            {!expensiveError && expensive === null && <Empty colSpan={4}>Loading…</Empty>}
            {!expensiveError && expensive !== null && !expensive.length && (
              <Empty colSpan={4}>No session with 30 measured seconds in this window.</Empty>
            )}
          </Table>
        </Card>

        <ModalityMix summary={summary} />
      </div>
    </div>
  );
};

const drillFor = (dimension: UsageDimension, key: string | null): Record<string, string | undefined> => {
  if (!key) return {};
  const map: Partial<Record<UsageDimension, string>> = {
    agent: 'agent', org: 'org', provider: 'provider', feature: 'feature',
    channel: 'channel', modality: 'modality',
  };
  const param = map[dimension];
  return param ? { [param]: key } : {};
};

/**
 * The comparison the previous dashboard could not make at all: the same
 * agents, the same window, phone against screen, on the units that matter.
 */
const ChannelComparison: React.FC<{
  rows: UsageChannelRow[];
  onDrill: (patch: Record<string, string | undefined>) => void;
}> = ({ rows, onDrill }) => {
  const { ccy, toDisplay } = useCurrency();
  if (!rows.length) return <Notice>No sessions in this window. Widen the range or clear a filter.</Notice>;

  const maxPerSession = Math.max(...rows.map((r) => r.cost_per_unit ?? 0), 0.0001);
  const maxPerMinute = Math.max(...rows.map((r) => r.cost_per_minute ?? 0), 0.0001);

  return (
    <Card
      title="Channel economics"
      subtitle="Phone against screen, on the same agents and the same window — per connected call, per session, per message"
      right={<ExportCsv onClick={() => downloadCsv('channel_economics', [
        { key: 'channel', label: 'channel' }, { key: 'unit_label', label: 'unit' }, { key: 'units', label: 'units' },
        { key: 'sessions', label: 'sessions' }, { key: 'rated_minutes', label: 'minutes' },
        { key: 'cost_per_unit', label: `cost_per_unit_${ccy}`, get: (r) => r.cost_per_unit == null ? '' : toDisplay(r.cost_per_unit as number) },
        { key: 'cost_per_minute', label: `cost_per_minute_${ccy}`, get: (r) => r.cost_per_minute == null ? '' : toDisplay(r.cost_per_minute as number) },
        { key: 'revenue_usd', label: `revenue_${ccy}`, get: (r) => toDisplay(r.revenue_usd as number) },
        { key: 'credits_billable', label: 'credits_billable' }, { key: 'margin_pct', label: 'margin_pct' },
      ], rows as unknown as Record<string, unknown>[])} />}
      bodyClassName=""
    >
      <Table
        head={
          <tr>
            <Th>Channel</Th>
            <Th align="right">Units</Th>
            <Th align="right">Measured time</Th>
            <Th align="right">Cost / unit</Th>
            <Th className="w-28" />
            <Th align="right">Cost / minute</Th>
            <Th className="w-28" />
            <Th align="right">Billed</Th>
            <Th align="right">Margin</Th>
          </tr>
        }
      >
        {rows.map((c) => (
          <tr key={c.channel} className="cursor-pointer hover:bg-gray-50" onClick={() => onDrill({ channel: c.channel })}>
            <Td>
              <div className="flex items-center gap-2">
                <Chip kind="channel" value={c.channel} />
                <span className="text-gray-900">{CHANNEL_LABEL[c.channel] ?? c.channel}</span>
              </div>
            </Td>
            <Td align="right" className="tabular-nums">{int(c.units)}<div className="text-[10px] font-normal text-gray-400">{c.unit_label === 'call' ? 'connected calls' : `${c.unit_label}s`}</div></Td>
            <Td align="right" className="whitespace-nowrap tabular-nums text-gray-600">
              {minutes(c.rated_minutes)}
              <div className="text-[10px] font-normal text-gray-400">
                {c.channel === 'phone' ? 'carrier talk time' : 'engaged time'}
              </div>
            </Td>
            <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={c.cost_per_unit} inr={c.cost_per_unit_inr} /></Td>
            <Td><ShareBar pct={((c.cost_per_unit ?? 0) / maxPerSession) * 100} color={CHANNEL_COLOR[c.channel]} /></Td>
            <Td align="right" className="tabular-nums font-medium text-gray-900"><Money value={c.cost_per_minute} inr={c.cost_per_minute_inr} /></Td>
            <Td><ShareBar pct={((c.cost_per_minute ?? 0) / maxPerMinute) * 100} color={CHANNEL_COLOR[c.channel]} /></Td>
            <Td align="right" className="tabular-nums text-gray-700">
              <Money value={c.revenue_usd} bare />
              <div className="text-[10px] font-normal text-gray-400">{int(c.credits_billable)} cr</div>
            </Td>
            <Td align="right" className={`tabular-nums font-medium ${
              c.margin_pct == null ? 'text-gray-400' : c.margin_pct < 0 ? 'text-rose-600' : 'text-emerald-600'
            }`}>
              {c.margin_pct == null ? '—' : pct(c.margin_pct)}
              <div className="text-[10px] font-normal text-gray-400"><Money value={c.margin_usd} bare /></div>
            </Td>
          </tr>
        ))}
      </Table>
      {rows.some((r) => r.unpriced_sessions > 0) && (
        <div className="border-t border-gray-100 px-4 py-2">
          <Notice tone="warn">
            {int(rows.reduce((a, r) => a + r.unpriced_sessions, 0))} sessions resolved to no rate card
            and bill nothing. Add a card for their channel on the Rate card tab.
          </Notice>
        </div>
      )}
    </Card>
  );
};

/**
 * What each channel is made of. A phone conversation and a screen
 * conversation cost different amounts largely because they are different
 * mixtures, and the mixture is the actionable part.
 */
const ModalityMix: React.FC<{ summary: UsageReportSummary | null }> = ({ summary }) => {
  const { fmt } = useCurrency();
  const rows = summary?.by_channel ?? [];
  return (
    <Card title="Cost mix by channel" subtitle="What each channel's spend is made of">
      <div className="space-y-4">
        {rows.map((c) => {
          const parts: [string, number][] = [
            ['llm', c.llm_cost], ['tts', c.tts_cost],
            ['stt', c.stt_cost], ['telephony', c.telephony_cost],
          ];
          const total = parts.reduce((a, [, v]) => a + v, 0) || 1;
          return (
            <div key={c.channel}>
              <div className="mb-1.5 flex items-baseline justify-between">
                <span className="flex items-center gap-2 text-sm text-gray-800">
                  <Chip kind="channel" value={c.channel} />
                  {CHANNEL_LABEL[c.channel] ?? c.channel}
                </span>
                <span className="text-xs tabular-nums text-gray-500"><Money value={c.cost_usd} inr={c.cost_inr} bare /></span>
              </div>
              <div className="flex h-3 w-full overflow-hidden rounded">
                {parts.map(([k, v]) => v > 0 && (
                  <div
                    key={k}
                    style={{ width: `${(v / total) * 100}%`, background: MODALITY_COLOR[k as keyof typeof MODALITY_COLOR] }}
                    title={`${MODALITY_LABEL[k as keyof typeof MODALITY_LABEL]}: ${fmt(v)} (${((v / total) * 100).toFixed(0)}%)`}
                  />
                ))}
              </div>
              <div className="mt-1 flex flex-wrap gap-x-3 text-[10px] text-gray-500">
                {parts.map(([k, v]) => v > 0 && (
                  <span key={k}>
                    {MODALITY_LABEL[k as keyof typeof MODALITY_LABEL]} {((v / total) * 100).toFixed(0)}%
                  </span>
                ))}
              </div>
            </div>
          );
        })}
        {!rows.length && <Empty />}
      </div>
    </Card>
  );
};
